'use client';

import { useEffect, useState } from 'react';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useIsHydrated } from '@packages/shared/src/hooks/useIsHydrated/useIsHydrated';
import { useIntl } from 'react-intl';
import { getLocalizedPath } from '@packages/shared/src/utils/getLocalizedPath/getLocalizedPath';
import { PageMetaData } from '@packages/modules/src/PageMetaData/PageMetaData';
import { CustomerInvoices } from '@packages/order/src/components/CustomerInvoices/CustomerInvoices.client';
import { BreadcrumbsLink } from '@packages/shared/src/ui/Breadcrumbs/Breadcrumbs';
import { Link } from '@packages/modules/src/Link/Link';
import { InvoiceDetail } from '@packages/order/src/components/Invoice/InvoiceDetail.client';

/**
 * Parse hash parameters from URL
 * Example: #invoicenumber=R01100000010&invoicedate=2025-12-05
 * Note: Parameter keys are case-insensitive
 */
const parseHashParams = (): { invoicenumber?: string; invoicedate?: string } => {
  if (typeof window === 'undefined') return {};

  const hashParams = new URLSearchParams(window.location.hash.substring(1));

  // URLSearchParams is case-sensitive, so we need to iterate and normalize keys
  let invoicenumber: string | undefined;
  let invoicedate: string | undefined;

  hashParams.forEach((value, key) => {
    const normalizedKey = key.toLowerCase();
    if (normalizedKey === 'invoicenumber') {
      invoicenumber = value;
    } else if (normalizedKey === 'invoicedate') {
      invoicedate = value;
    }
  });

  return { invoicenumber, invoicedate };
};

const InvoicesPage = () => {
  const {
    order: {
      paths: { myAccountBookings },
    },
  } = useConfig();
  const { locale, formatMessage } = useIntl();
  const [invoiceToOpen, setInvoiceToOpen] = useState<string | null>(null);
  // `undefined` until the hash has been read on the first client render, `null` without a number
  const [hashInvoiceNumber, setHashInvoiceNumber] = useState<string | null>();
  const { isHydrated } = useIsHydrated();

  if (isHydrated && hashInvoiceNumber === undefined) {
    const invoiceNumberFromHash = parseHashParams().invoicenumber ?? null;
    setHashInvoiceNumber(invoiceNumberFromHash);
    // Auto-open the invoice dialog with the specified invoice number
    setInvoiceToOpen(invoiceNumberFromHash);
  }

  useEffect(() => {
    if (hashInvoiceNumber) {
      // Clear hash from URL to avoid re-triggering on navigation
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, [hashInvoiceNumber]);

  return (
    <>
      <PageMetaData page={{ type: 'myaccount', template: 'invoices' }} />
      <BreadcrumbsLink asChild className="text-size-body-sm flex items-center gap-1">
        <Link href={getLocalizedPath(myAccountBookings, locale)}>
          <span className="icon-emp-arrow-mini-left size-4" />
          {formatMessage({
            id: 'order.backto.bookings',
            defaultMessage: 'Zurück zu Meine Buchungen',
          })}
        </Link>
      </BreadcrumbsLink>
      <h1>
        {formatMessage({
          id: 'InvoicesPage.Headline',
          defaultMessage: 'Meine Rechnungen',
        })}
      </h1>
      <p>
        {formatMessage({
          id: 'InvoicesPage.Subheadline',
          defaultMessage: 'Hier findest du eine Übersicht aller Rechnungen.',
        })}
      </p>
      <CustomerInvoices />
      {invoiceToOpen && (
        <InvoiceDetail onClose={() => setInvoiceToOpen(null)} invoiceNumber={invoiceToOpen} />
      )}
    </>
  );
};

// eslint-disable-next-line import-x/no-default-export
export default InvoicesPage;
